/**
 * Shop owner analytics tab: KPIs, charts, filters, access code, report summary, reviews, insights.
 */
import React from 'react';
import { FaChartBar } from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { http } from '../../lib/http';
import {
  BarChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  Legend,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { generateRecommendations } from '../../utils/analytics/recommendations';
import { getPreviousPeriod, calculatePeriodComparison } from '../../utils/analytics/comparativeAnalysis';
import { forecastRevenue, detectAnomalies, predictBookingTrend } from '../../utils/analytics/predictiveInsights';
import { cleanText } from './utils';
import {
  CURRENCY,
  toDate,
  startOf,
  add,
  toDateKey,
  isSame,
  isSameOrAfter,
  isSameOrBefore,
  firstDefined,
  num,
  normalize,
  formatLabel,
} from './analyticsHelpers';

const KpiTile = ({ label, value, accent = '' }) => (
  <article className={`kpi-tile ${accent}`}>
    <p className="kpi-value">{cleanText(value)}</p>
    <p className="kpi-label">{label}</p>
  </article>
);

export default React.memo(function AnalyticsTab({ appts, biz }) {
    const raw = React.useMemo(() => (Array.isArray(appts) ? appts : []).map(normalize), [appts]);

    const [range, setRange] = React.useState('month'); // day|week|month|year
    const [startDate, setStartDate] = React.useState('');
    const [endDate, setEndDate] = React.useState('');
    const [staffFilter, setStaffFilter] = React.useState('');
    const [serviceFilter, setServiceFilter] = React.useState('');
    
    // Analytics access code state
    const [isPasscodeValidated, setIsPasscodeValidated] = React.useState(false);
    const [passcode, setPasscode] = React.useState('');
    const [passcodeError, setPasscodeError] = React.useState('');
    const [validatingPasscode, setValidatingPasscode] = React.useState(false);
    
    // Report Summary state
    const [showReportSummary, setShowReportSummary] = React.useState(false);
    
    // Ratings & Reviews state
    const [reviews, setReviews] = React.useState([]);
    const [loadingReviews, setLoadingReviews] = React.useState(false);
    const [showReviews, setShowReviews] = React.useState(false);

    // Expandable groups (Overview, Bookings, Revenue, Services, Staff, Feedback, Insights)
    const [expandedGroups, setExpandedGroups] = React.useState({
      overview: true,
      bookings: true,
      revenue: true,
      services: true,
      staff: true,
      feedback: true,
      insights: true,
    });
    const [comparisonPeriod, setComparisonPeriod] = React.useState(null); // 'wow' | 'mom' | 'yoy' | null
    
    const { t, lang } = useI18n();

    // ==================== UPDATED DATE PICKER FIELD ====================
    const DatePickerField = ({ value, onChange, placeholder, ariaLabel }) => {
      const [open, setOpen] = React.useState(false);
      const [viewDate, setViewDate] = React.useState(() => {
        const base = toDate(value) || new Date();
        return Number.isNaN(base?.getTime?.()) ? new Date() : base;
      });
      const containerRef = React.useRef(null);
      const iconRef = React.useRef(null);
      const { lang } = useI18n();

      const locale = 'en-US'; /* numbers always in English (0-9) in Arabic version too */

      const displayText = React.useMemo(() => {
        if (!value) return placeholder;
        const d = toDate(value);
        if (!d) return placeholder;
        return d.toLocaleDateString(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }, [value, placeholder, locale]);

      const monthLabel = React.useMemo(() => {
        return new Intl.DateTimeFormat(locale, {
          month: 'long',
          year: 'numeric'
        }).format(viewDate);
      }, [viewDate, locale]);

      const weekdayLabels = React.useMemo(() => {
        const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
        const base = new Date(2021, 0, 4);
        return Array.from({ length: 7 }, (_, i) => {
          const d = new Date(base);
          d.setDate(base.getDate() + i);
          return formatter.format(d);
        });
      }, [locale]);

      const dayCells = React.useMemo(() => {
        const firstOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
        const offset = (firstOfMonth.getDay() + 6) % 7;
        const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
        const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
        return Array.from({ length: totalCells }, (_, index) => {
          const calendarDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), index - offset + 1);
          const iso = toDateKey(calendarDate);
          const isCurrentMonth = calendarDate.getMonth() === viewDate.getMonth();
          return {
            date: calendarDate,
            iso,
            day: calendarDate.getDate(),
            isCurrentMonth
          };
        });
      }, [viewDate]);

      const todayKey = React.useMemo(() => toDateKey(new Date()), []);
      const selectedKey = value || '';

      React.useEffect(() => {
        if (!open) return;
        const base = toDate(value) || new Date();
        if (base && !Number.isNaN(base.getTime())) {
          setViewDate(base);
        }
      }, [open, value]);

      React.useEffect(() => {
        const handleClickOutside = (event) => {
          if (containerRef.current && !containerRef.current.contains(event.target) &&
              iconRef.current && !iconRef.current.contains(event.target)) {
            setOpen(false);
          }
        };
        const handleEsc = (event) => {
          if (event.key === 'Escape') {
            setOpen(false);
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
          document.removeEventListener('touchstart', handleClickOutside);
          document.removeEventListener('keydown', handleEsc);
        };
      }, []);

      const goMonth = React.useCallback((delta) => {
        setViewDate(prev => add(prev, 'month', delta));
      }, []);

      const handleSelect = React.useCallback((dateObj) => {
        const iso = toDateKey(dateObj);
        if (iso) {
          onChange(iso);
          setOpen(false);
        }
      }, [onChange]);

      // Toggle calendar when icon is clicked
      const toggleCalendar = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(prev => !prev);
      };

      return (
        <div 
          className={`date-picker-field ${lang === 'ar' ? 'rtl' : ''}`} 
          ref={containerRef}
        >
          {/* Clickable calendar icon - NO RECTANGLE BUTTON */}
          <span 
            ref={iconRef}
            className="date-picker-icon"
            onClick={toggleCalendar}
            role="button"
            tabIndex={0}
            aria-label={ariaLabel || placeholder}
            aria-expanded={open}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleCalendar(e);
              }
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
              <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
              <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </span>

          {/* Selected date badge - shows when date is selected */}
          {value && (
            <span className="selected-date-badge has-date">
              {displayText}
            </span>
          )}

          {/* Clear button */}
          {value && (
            <button
              type="button"
              className="date-picker-clear"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              aria-label="Clear date"
            >
              ×
            </button>
          )}

          {/* Calendar popover */}
          {open && (
            <div className={`date-picker-popover ${lang === 'ar' ? 'rtl' : ''}`} role="dialog">
              <div className="date-picker-header">
                <button type="button" onClick={() => goMonth(-1)} aria-label="Previous month">
                  ‹
                </button>
                <div className="date-picker-month">{monthLabel}</div>
                <button type="button" onClick={() => goMonth(1)} aria-label="Next month">
                  ›
                </button>
              </div>
              <div className="date-picker-weekdays">
                {weekdayLabels.map((label, idx) => (
                  <span key={idx} className="date-picker-weekday">{label}</span>
                ))}
              </div>
              <div className="date-picker-grid">
                {dayCells.map(({ date, iso, day, isCurrentMonth }) => {
                  const isSelected = selectedKey === iso;
                  const isToday = todayKey === iso;
                  return (
                    <button
                      key={iso}
                      type="button"
                      className={[
                        'date-picker-day',
                        isCurrentMonth ? 'current' : 'muted',
                        isSelected ? 'selected' : '',
                        isToday ? 'today' : ''
                      ].filter(Boolean).join(' ')}
                      onClick={() => handleSelect(date)}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    };
    // ==================== END UPDATED DATE PICKER FIELD ====================
    
    // Reset showReportSummary when filters change
    React.useEffect(() => {
      setShowReportSummary(false);
    }, [startDate, endDate, staffFilter, serviceFilter, range]);
    
    // Load reviews when business data is available
    React.useEffect(() => {
      const loadReviews = async () => {
        if (!biz?._id && !biz?.name) return;
        
        setLoadingReviews(true);
        try {
          let reviewsData = [];
          
          // Try providerId first
          if (biz._id) {
            try {
              const url = `/api/reviews?providerId=${biz._id}&_t=${Date.now()}`;
              const data = await http.getJson(url);
              reviewsData = Array.isArray(data) ? data : [];
            } catch (err) {
              // Reviews by providerId failed
            }
          }
          
          // If no reviews found by ID, try by name
          if (reviewsData.length === 0 && biz.name) {
            try {
              const url = `/api/reviews?provider=${encodeURIComponent(biz.name)}&_t=${Date.now()}`;
              const data = await http.getJson(url);
              reviewsData = Array.isArray(data) ? data : [];
            } catch (err) {
              // Reviews by provider name failed
            }
          }
          
          setReviews(reviewsData);
        } catch (err) {
          setReviews([]);
        } finally {
          setLoadingReviews(false);
        }
      };
      
      loadReviews();
    }, [biz?._id, biz?.name]);

    const staffList = React.useMemo(
      () => Array.from(new Set(raw.map(a => a.staff).filter(Boolean))).sort(),
      [raw]
    );
    const serviceList = React.useMemo(
      () => Array.from(new Set(raw.map(a => a.serviceName).filter(Boolean))).sort(),
      [raw]
    );

    // Access code validation
    const validatePasscode = async () => {
      if (!passcode || passcode.length !== 4) {
        setPasscodeError(t('ownerAnalyticsCodeRequired') || 'Please enter 4 digits');
        return;
      }

      setValidatingPasscode(true);
      setPasscodeError('');

      try {
        const response = await fetch('/api/shopowner/validate-analytics-passcode', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            shopName: biz?.name,
            passcode: passcode
          })
        });

        const data = await response.json();

        if (response.ok && data.valid) {
          setIsPasscodeValidated(true);
          setPasscodeError('');
        } else {
          setPasscodeError(data.error || t('ownerAnalyticsCodeInvalid') || 'Invalid entry');
        }
      } catch (error) {
        setPasscodeError(t('ownerAnalyticsCodeFailed') || 'Validation failed. Please try again.');
      } finally {
        setValidatingPasscode(false);
      }
    };

    const filtered = React.useMemo(() => {
      let d = raw.slice();
      if (staffFilter) d = d.filter(a => a.staff === staffFilter);
      if (serviceFilter) d = d.filter(a => a.serviceName === serviceFilter);

      const hasCustom = !!(startDate || endDate);
      if (hasCustom) {
        const s = startDate ? startOf(toDate(startDate), 'day') : null;
        const e = endDate ? startOf(toDate(endDate), 'day') : null;
        d = d.filter(a => {
          if (!a.date) return true; // keep undated
          if (s && !isSameOrAfter(a.date, s, 'day')) return false;
          if (e && !isSameOrBefore(a.date, e, 'day')) return false;
          return true;
        });
      } else {
        const cfg = { day: { unit: 'day', len: 30 }, week: { unit: 'week', len: 12 }, month: { unit: 'month', len: 12 }, year: { unit: 'year', len: 5 } }[range];
        const from = add(startOf(new Date(), cfg.unit), cfg.unit, -(cfg.len - 1));
        d = d.filter(a => {
          if (!a.date) return true;
          return isSameOrAfter(a.date, from, cfg.unit);
        });
      }
      return d;
    }, [raw, range, startDate, endDate, staffFilter, serviceFilter]);

    const kpis = React.useMemo(() => {
      const total = filtered.length;
      const pending = filtered.filter(a => a.status === 'pending').length;
      const accepted = filtered.filter(a => a.status === 'accepted').length;
      const declined = filtered.filter(a => a.status === 'declined').length;
      const cancelled = filtered.filter(a => a.status === 'cancelled').length;
      const completed = filtered.filter(a => a.status === 'completed').length;
      const revenue = filtered.filter(a => a.status === 'completed').reduce((s, a) => s + a.price, 0);
      const avgBookingValue = completed > 0 ? revenue / completed : 0;
      const completionRate = total > 0 ? (completed / total * 100) : 0;
      const cancellationRate = total > 0 ? ((cancelled + declined) / total * 100) : 0;
      return { 
        total, pending, accepted, declined, cancelled, completed, revenue, 
        avgBookingValue, completionRate, cancellationRate 
      };
    }, [filtered]);

    const dated = React.useMemo(() => filtered.filter(a => a.date), [filtered]);

    const makeSeries = React.useCallback((unit, points) => {
      const nowStart = startOf(new Date(), unit);
      return Array.from({ length: points }, (_, i) => {
        const bucket = add(nowStart, unit, i - (points - 1));
        const label = formatLabel(bucket, unit);
        const inBucket = dated.filter(a => isSame(a.date, bucket, unit));
        const books = inBucket.length;
        const revenue = inBucket.filter(a => a.status === 'completed').reduce((s, a) => s + a.price, 0);
        const statuses = ['pending', 'accepted', 'declined', 'cancelled', 'completed']
          .reduce((o, st) => { o[st] = inBucket.filter(a => a.status === st).length; return o; }, {});
        return { label, books, revenue, ...statuses };
      });
    }, [dated]);

    const lineData = React.useMemo(() => {
      if (startDate || endDate) {
        const minD = dated.length ? dated.reduce((m, a) => (!m || a.date < m ? a.date : m), null) : null;
        const maxD = dated.length ? dated.reduce((m, a) => (!m || a.date > m ? a.date : m), null) : null;
        const min = startDate ? startOf(toDate(startDate), 'day') : minD;
        const max = endDate ? startOf(toDate(endDate), 'day') : maxD;
        if (!min || !max) return [];
        const days = Math.max(Math.round((max - min) / 86400000) + 1, 1);
        return Array.from({ length: days }, (_, i) => {
          const d = add(min, 'day', i);
          const label = formatLabel(d, 'day');
          const inBucket = dated.filter(a => isSame(a.date, d, 'day'));
          const books = inBucket.length;
          const revenue = inBucket.filter(a => a.status === 'completed').reduce((s, a) => s + a.price, 0);
          const statuses = ['pending', 'accepted', 'declined', 'cancelled', 'completed']
            .reduce((o, st) => { o[st] = inBucket.filter(a => a.status === st).length; return o; }, {});
          return { label, books, revenue, ...statuses };
        });
      }
      return ({
        day: makeSeries('day', 30),
        week: makeSeries('week', 12),
        month: makeSeries('month', 12),
        year: makeSeries('year', 5),
      }[range]);
    }, [range, startDate, endDate, dated, makeSeries]);

    const topServices = React.useMemo(() => {
      const counts = {};
      filtered.forEach(a => { const k = a.serviceName || 'Unknown'; counts[k] = (counts[k] || 0) + 1; });
      return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);
    }, [filtered]);

    const statusDistribution = React.useMemo(
      () => [
        { name: t('dashStatPending'), value: kpis.pending },
        { name: t('ownerAccepted'), value: kpis.accepted },
        { name: t('ownerDecline'), value: kpis.declined },
        { name: t('ownerCancelled'), value: kpis.cancelled },
        { name: t('dashStatCompleted'), value: kpis.completed },
      ].filter(x => x.value > 0),
      [kpis, t]
    );

    const conversionFunnelData = React.useMemo(
      () => [
        { name: t('dashStatPending'), value: kpis.pending, fill: '#FFA500' },
        { name: t('ownerAccepted'), value: kpis.accepted, fill: '#18442A' },
        { name: t('dashStatCompleted'), value: kpis.completed, fill: '#4CAF50' },
      ].filter(x => x.value > 0),
      [kpis, t]
    );

    const overviewHealthData = React.useMemo(
      () => [
        { name: lang === 'ar' ? 'الإكمال' : 'Completion', value: kpis.completionRate },
        { name: lang === 'ar' ? 'الإلغاء' : 'Cancellation', value: kpis.cancellationRate },
      ],
      [kpis.completionRate, kpis.cancellationRate, lang]
    );

    const ratingDistribution = React.useMemo(() => {
      const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      reviews.forEach(r => {
        const star = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 0)));
        counts[star] = (counts[star] || 0) + 1;
      });
      return [1, 2, 3, 4, 5].map(star => ({ name: `${star} ★`, value: counts[star] || 0 }));
    }, [reviews]);

    // ── NEW DATASETS ─────────────────────────────────────────────────────────────
    const bookingsByStaff = React.useMemo(() => {
      const counts = {};
      filtered.forEach(a => { const k = a.staff || 'Unassigned'; counts[k] = (counts[k] || 0) + 1; });
      return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);
    }, [filtered]);

    const revenueByService = React.useMemo(() => {
      const sums = {};
      filtered.forEach(a => {
        if (a.status !== 'completed') return; // why: only realized revenue
        const k = a.serviceName || 'Unknown';
        sums[k] = (sums[k] || 0) + (a.price || 0);
      });
      return Object.entries(sums).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);
    }, [filtered]);

    const revenueByStaff = React.useMemo(() => {
      const sums = {};
      filtered.forEach(a => {
        if (a.status !== 'completed') return; // why: only realized revenue
        const k = a.staff || 'Unassigned';
        sums[k] = (sums[k] || 0) + (a.price || 0);
      });
      return Object.entries(sums).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 6);
    }, [filtered]);
    
    // Calculate date-filtered appointments for ranking (without staff/service filters)
    const dateFilteredAppointments = React.useMemo(() => {
      let d = raw.slice();
      const hasCustom = !!(startDate || endDate);
      if (hasCustom) {
        const s = startDate ? startOf(toDate(startDate), 'day') : null;
        const e = endDate ? startOf(toDate(endDate), 'day') : null;
        d = d.filter(a => {
          if (!a.date) return true;
          if (s && !isSameOrAfter(a.date, s, 'day')) return false;
          if (e && !isSameOrBefore(a.date, e, 'day')) return false;
          return true;
        });
      } else {
        const cfg = { day: { unit: 'day', len: 30 }, week: { unit: 'week', len: 12 }, month: { unit: 'month', len: 12 }, year: { unit: 'year', len: 5 } }[range];
        const from = add(startOf(new Date(), cfg.unit), cfg.unit, -(cfg.len - 1));
        d = d.filter(a => {
          if (!a.date) return true;
          return isSameOrAfter(a.date, from, cfg.unit);
        });
      }
      return d;
    }, [raw, range, startDate, endDate]);
    
    // Calculate all staff and service rankings
    const { allStaffBookings, allStaffRevenue, allServiceBookings, allServiceRevenue } = React.useMemo(() => {
      const staffBookings = {};
      const staffRevenue = {};
      const serviceBookings = {};
      const serviceRevenue = {};
      
      dateFilteredAppointments.forEach(a => {
        const staffName = a.staff || 'Unassigned';
        const serviceName = a.serviceName || 'Unknown';
        
        staffBookings[staffName] = (staffBookings[staffName] || 0) + 1;
        serviceBookings[serviceName] = (serviceBookings[serviceName] || 0) + 1;
        
        if (a.status === 'completed') {
          staffRevenue[staffName] = (staffRevenue[staffName] || 0) + (a.price || 0);
          serviceRevenue[serviceName] = (serviceRevenue[serviceName] || 0) + (a.price || 0);
        }
      });
      
      return { allStaffBookings: staffBookings, allStaffRevenue: staffRevenue, allServiceBookings: serviceBookings, allServiceRevenue: serviceRevenue };
    }, [dateFilteredAppointments]);
    
    // Calculate ranks
    const staffBookingsRank = staffFilter ? (() => {
      const sorted = Object.entries(allStaffBookings).sort((a, b) => b[1] - a[1]);
      return sorted.findIndex(([name]) => name === staffFilter) + 1;
    })() : null;
    
    const staffRevenueRank = staffFilter ? (() => {
      const sorted = Object.entries(allStaffRevenue).sort((a, b) => b[1] - a[1]);
      return sorted.findIndex(([name]) => name === staffFilter) + 1;
    })() : null;
    
    const serviceBookingsRank = serviceFilter ? (() => {
      const sorted = Object.entries(allServiceBookings).sort((a, b) => b[1] - a[1]);
      return sorted.findIndex(([name]) => name === serviceFilter) + 1;
    })() : null;
    
    const serviceRevenueRank = serviceFilter ? (() => {
      const sorted = Object.entries(allServiceRevenue).sort((a, b) => b[1] - a[1]);
      return sorted.findIndex(([name]) => name === serviceFilter) + 1;
    })() : null;
    
    // Top revenue services
    const topRevenueServices = React.useMemo(() => {
      const sums = {};
      filtered.forEach(a => {
        if (a.status !== 'completed') return;
        const k = a.serviceName || 'Unknown';
        sums[k] = (sums[k] || 0) + (a.price || 0);
      });
      return Object.entries(sums).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 5);
    }, [filtered]);

    // ── PEAK HOURS ANALYSIS ─────────────────────────────────────────────────────────────
    const peakHours = React.useMemo(() => {
      const hourlyData = Array.from({ length: 24 }, (_, i) => ({ hour: i, bookings: 0, revenue: 0 }));
      
      filtered.forEach(a => {
        if (a.date) {
          const hour = new Date(a.date).getHours();
          hourlyData[hour].bookings += 1;
          if (a.status === 'completed') {
            hourlyData[hour].revenue += a.price || 0;
          }
        }
      });
      
      return hourlyData.map(h => ({
        ...h,
        label: `${h.hour.toString().padStart(2, '0')}:00`,
        revenue: Math.round(h.revenue)
      }));
    }, [filtered]);

    // ── SERVICE PERFORMANCE METRICS ─────────────────────────────────────────────────────────────
    const servicePerformance = React.useMemo(() => {
      const serviceData = {};
      
      filtered.forEach(a => {
        const serviceName = a.serviceName || 'Unknown';
        if (!serviceData[serviceName]) {
          serviceData[serviceName] = {
            name: serviceName,
            totalBookings: 0,
            completedBookings: 0,
            totalRevenue: 0,
            avgPrice: 0,
            completionRate: 0,
            cancellationRate: 0
          };
        }
        
        serviceData[serviceName].totalBookings += 1;
        if (a.status === 'completed') {
          serviceData[serviceName].completedBookings += 1;
          serviceData[serviceName].totalRevenue += a.price || 0;
        }
      });
      
      return Object.values(serviceData).map(service => ({
        ...service,
        avgPrice: service.completedBookings > 0 ? Math.round(service.totalRevenue / service.completedBookings) : 0,
        completionRate: service.totalBookings > 0 ? (service.completedBookings / service.totalBookings) * 100 : 0,
        cancellationRate: service.totalBookings > 0 ? ((service.totalBookings - service.completedBookings) / service.totalBookings) * 100 : 0
      })).sort((a, b) => b.totalRevenue - a.totalRevenue);
    }, [filtered]);

    // Revenue by day of week
    const revenueByDayOfWeek = React.useMemo(() => {
      const days = [0, 1, 2, 3, 4, 5, 6];
      const dayNames = lang === 'ar' ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return days.map((day, i) => {
        const rev = filtered.filter(a => a.date && a.status === 'completed' && new Date(a.date).getDay() === day).reduce((s, a) => s + (a.price || 0), 0);
        return { name: dayNames[i], value: Math.round(rev), day };
      });
    }, [filtered, lang]);

    // Revenue by month/quarter (last 12 months)
    const revenueByMonthQuarter = React.useMemo(() => {
      const now = new Date();
      return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
        const label = d.toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', { month: 'short', year: '2-digit' });
        const rev = filtered.filter(a => a.date && a.status === 'completed' && a.date.getMonth() === d.getMonth() && a.date.getFullYear() === d.getFullYear()).reduce((s, a) => s + (a.price || 0), 0);
        return { label, value: Math.round(rev), month: d.getMonth(), year: d.getFullYear() };
      });
    }, [filtered, lang]);

    // Previous period for comparison (WoW / MoM / YoY)
    const { start: periodStart, end: periodEnd } = React.useMemo(() => {
      if (startDate && endDate) {
        return { start: toDate(startDate), end: toDate(endDate) };
      }
      const cfg = { day: { unit: 'day', len: 30 }, week: { unit: 'week', len: 12 }, month: { unit: 'month', len: 12 }, year: { unit: 'year', len: 5 } }[range];
      const from = add(startOf(new Date(), cfg.unit), cfg.unit, -(cfg.len - 1));
      const to = new Date();
      return { start: from, end: to };
    }, [range, startDate, endDate]);

    const comparisonPeriodType = comparisonPeriod === 'wow' ? 'week' : comparisonPeriod === 'mom' ? 'month' : comparisonPeriod === 'yoy' ? 'year' : null;
    const previousPeriodBounds = React.useMemo(() => comparisonPeriodType ? getPreviousPeriod(periodStart, periodEnd, comparisonPeriodType) : null, [comparisonPeriodType, periodStart, periodEnd]);

    const previousPeriodData = React.useMemo(() => {
      if (!previousPeriodBounds || !raw.length) return [];
      const { start, end } = previousPeriodBounds;
      return raw.filter(a => a.date && isSameOrAfter(a.date, start, 'day') && isSameOrBefore(a.date, end, 'day'));
    }, [raw, previousPeriodBounds]);

    const periodComparison = React.useMemo(() => {
      if (!comparisonPeriodType || !previousPeriodBounds) return null;
      return calculatePeriodComparison(filtered, previousPeriodData, comparisonPeriodType);
    }, [comparisonPeriodType, filtered, previousPeriodData, previousPeriodBounds]);

    const recommendationsList = React.useMemo(() => generateRecommendations(
      { ...kpis, total: kpis.total, totalAppointments: kpis.total },
      { lang }
    ), [kpis, lang]);

    const forecastData = React.useMemo(() => forecastRevenue(filtered.filter(a => a.status === 'completed'), 1), [filtered]);
    const anomaliesList = React.useMemo(() => detectAnomalies(filtered, 'revenue'), [filtered]);
    const bookingTrendPrediction = React.useMemo(() => predictBookingTrend(filtered, 7), [filtered]);

    const { customerLifetimeValue, customerAppointmentsCount } = React.useMemo(() => {
      const completed = filtered.filter(a => a.status === 'completed');
      const byClient = {};
      completed.forEach(a => {
        const id = a.client || a.clientName || 'Anonymous';
        if (!byClient[id]) byClient[id] = { count: 0, revenue: 0 };
        byClient[id].count += 1;
        byClient[id].revenue += a.price || 0;
      });
      const clients = Object.keys(byClient).length;
      const totalRev = completed.reduce((s, a) => s + (a.price || 0), 0);
      return {
        customerAppointmentsCount: clients,
        customerLifetimeValue: clients > 0 ? Math.round(totalRev / clients) : 0,
      };
    }, [filtered]);

    const rangeLabel = (startDate || endDate)
      ? t('ownerCustomRange')
      : t({ day: 'ownerLast30Days', week: 'ownerLast12Weeks', month: 'ownerLast12Months', year: 'ownerLast5Years' }[range]);

    const exportCSV = () => {
      if (!filtered.length) return;
      const cols = ['date', 'status', 'serviceName', 'staff', 'client', 'price', 'source', 'cancellationReason', 'createdAt'];
      const rows = filtered.map(a =>
        cols.map(c => JSON.stringify(
          c === 'date' ? (a.date ? a.date.toISOString() : '')
            : c === 'createdAt' ? (a.createdAt ? a.createdAt.toISOString() : '')
              : a[c] ?? ''
        )).join(',')
      );
      const csv = [cols.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'appointments-analytics.csv'; a.click();
      URL.revokeObjectURL(url);
    };

    const exportPDF = () => {
      // Get the completion and cancellation rates for rating message
      const completionRate = kpis.total > 0 ? (kpis.completed / kpis.total * 100) : 0;
      const cancellationRate = kpis.total > 0 ? ((kpis.cancelled + kpis.declined) / kpis.total * 100) : 0;
      
      // Determine rating message
      let ratingMessage = '';
      
      if (completionRate >= 90 && cancellationRate < 10) {
        ratingMessage = lang === 'ar' 
          ? `أداء ممتاز! نسبة الإكمال ${completionRate.toFixed(1)}% ونسبة الإلغاء ${cancellationRate.toFixed(1)}% تشير إلى عمليات سلسة. استمر في هذا الأداء المتميز.`
          : `Excellent performance! A ${completionRate.toFixed(1)}% completion rate and ${cancellationRate.toFixed(1)}% cancellation rate indicate smooth operations. Keep up this outstanding work.`;
      } else if (completionRate >= 75 && cancellationRate < 15) {
        ratingMessage = lang === 'ar'
          ? `أداء جيد جداً. نسبة الإكمال ${completionRate.toFixed(1)}% قوية. تقليل الإلغاءات من ${cancellationRate.toFixed(1)}% سيعزز الربحية.`
          : `Very good performance. Your ${completionRate.toFixed(1)}% completion rate is strong. Reducing cancellations from ${cancellationRate.toFixed(1)}% will boost profitability.`;
      } else if (completionRate >= 60 && cancellationRate < 25) {
        ratingMessage = lang === 'ar'
          ? `أداء جيد. نسبة الإكمال ${completionRate.toFixed(1)}% قابلة للتحسين. ركز على تقليل الإلغاءات (${cancellationRate.toFixed(1)}%) وتحسين معدل الإكمال.`
          : `Good performance. Your ${completionRate.toFixed(1)}% completion rate has room for improvement. Focus on reducing cancellations (${cancellationRate.toFixed(1)}%) and improving completion rates.`;
      } else {
        ratingMessage = lang === 'ar'
          ? `يحتاج الأداء إلى تحسين. نسبة الإكمال ${completionRate.toFixed(1)}% ونسبة الإلغاء ${cancellationRate.toFixed(1)}% تتطلب اهتماماً فورياً. فكر في تحسين عملية الحجز والتواصل مع العملاء.`
          : `Performance needs improvement. A ${completionRate.toFixed(1)}% completion rate and ${cancellationRate.toFixed(1)}% cancellation rate require immediate attention. Consider improving booking processes and customer communication.`;
      }

      // Format date range
      const dateRangeText = startDate || endDate 
        ? `${startDate || t('ownerStart')} → ${endDate || t('ownerEnd')}`
        : (lang === 'ar' 
          ? (range === 'day' ? 'اليوم' : range === 'week' ? 'هذا الأسبوع' : range === 'month' ? 'هذا الشهر' : 'هذه السنة')
          : (range === 'day' ? 'Today' : range === 'week' ? 'This Week' : range === 'month' ? 'This Month' : 'This Year'));

      // Build HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>${t('ownerReportSummary')}</title>
            <style>
              @media print {
                @page { margin: 20mm; }
              }
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                color: #1f2937;
                ${lang === 'ar' ? 'direction: rtl; text-align: right;' : ''}
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 3px solid #18442A;
                padding-bottom: 20px;
              }
              .title {
                font-size: 24px;
                font-weight: bold;
                color: #18442A;
                margin-bottom: 10px;
              }
              .subtitle {
                font-size: 14px;
                color: #6b7280;
              }
              .section {
                margin: 20px 0;
              }
              .section-title {
                font-size: 18px;
                font-weight: bold;
                color: #18442A;
                margin-bottom: 10px;
              }
              .metadata {
                background: #f8fafc;
                padding: 15px;
                border-radius: 8px;
                margin: 15px 0;
              }
              .metadata-row {
                display: flex;
                justify-content: space-between;
                margin: 8px 0;
                ${lang === 'ar' ? 'flex-direction: row-reverse;' : ''}
              }
              .label {
                font-weight: bold;
                color: #6b7280;
              }
              .value {
                color: #1f2937;
              }
              .table {
                width: 100%;
                border-collapse: collapse;
                margin: 15px 0;
              }
              .table th, .table td {
                padding: 12px;
                text-align: ${lang === 'ar' ? 'right' : 'left'};
                border-bottom: 1px solid #e0e0e0;
              }
              .table th {
                background: #f8fafc;
                font-weight: bold;
                color: #6b7280;
              }
              .rating-box {
                background: rgba(0, 191, 166, 0.08);
                border-left: ${lang === 'ar' ? 'none' : '3px solid #18442A'};
                border-right: ${lang === 'ar' ? '3px solid #18442A' : 'none'};
                padding: 15px;
                margin: 15px 0;
                border-radius: 8px;
              }
              .footer {
                margin-top: 30px;
                padding-top: 20px;
                border-top: 1px solid #e0e0e0;
                text-align: center;
                color: #6b7280;
                font-size: 12px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="title">${t('ownerReportSummary')}</div>
              <div class="subtitle">${biz?.name || 'Business'} • ${new Date().toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>
            
            <div class="section">
              <div class="section-title">${t('ownerFiltersContext') || 'Filters'}</div>
              <div class="metadata">
                <div class="metadata-row">
                  <span class="label">${t('ownerDateRange')}:</span>
                  <span class="value">${dateRangeText}</span>
                </div>
                <div class="metadata-row">
                  <span class="label">${t('ownerStaffLabelShort')}:</span>
                  <span class="value">${staffFilter || t('ownerAllStaff')}</span>
                </div>
                <div class="metadata-row">
                  <span class="label">${t('ownerServiceLabelShort')}:</span>
                  <span class="value">${serviceFilter || t('ownerAllServices')}</span>
                </div>
              </div>
            </div>

            ${ratingMessage ? `
            <div class="section">
              <div class="section-title">${t('ownerPerformanceInsights')}</div>
              <div class="rating-box">
                <p style="margin: 0; line-height: 1.6; color: #1f2937;">${ratingMessage}</p>
              </div>
            </div>
            ` : ''}

            <div class="section">
              <div class="section-title">${t('ownerAnalyticsTitle')}</div>
              <table class="table">
                <tr>
                  <th>${t('ownerTotalAppointments')}</th>
                  <td>${kpis.total}</td>
                </tr>
                <tr>
                  <th>${t('dashStatCompleted')}</th>
                  <td style="color: #4CAF50;">${kpis.completed}</td>
                </tr>
                <tr>
                  <th>${t('dashStatPending')}</th>
                  <td style="color: #FFA500;">${kpis.pending}</td>
                </tr>
                <tr>
                  <th>${t('ownerAccepted')}</th>
                  <td style="color: #18442A;">${kpis.accepted}</td>
                </tr>
                <tr>
                  <th>${t('ownerCancellationRate')}</th>
                  <td style="color: #F44336;">${((kpis.cancelled + kpis.declined) / (kpis.total || 1) * 100).toFixed(1)}%</td>
                </tr>
                <tr>
                  <th>${t('ownerRevenueEGP')}</th>
                  <td>${kpis.revenue === 0 ? 'EGP 0' : CURRENCY(kpis.revenue)}</td>
                </tr>
                ${lang !== 'ar' ? `
                <tr>
                  <th>${t('ownerAvgBookingValue')}</th>
                  <td>${kpis.avgBookingValue === 0 ? 'EGP 0' : CURRENCY(kpis.avgBookingValue)}</td>
                </tr>
                <tr>
                  <th>${t('ownerCompletionRate')}</th>
                  <td>${kpis.completionRate.toFixed(1)}%</td>
                </tr>
                ` : ''}
              </table>
            </div>

            ${!serviceFilter && topServices.length > 0 ? `
            <div class="section">
              <div class="section-title">${t('ownerTopServicesByBookings')}</div>
              <table class="table">
                <tr>
                  <th>#</th>
                  <th>${t('ownerServiceLabelShort')}</th>
                  <th>${t('ownerAnalyticsTotal') || 'Total'}</th>
                </tr>
                ${topServices.map((s, i) => `
                  <tr>
                    <td>${i + 1}</td>
                    <td>${s.name}</td>
                    <td>${s.value}</td>
                  </tr>
                `).join('')}
              </table>
            </div>
            ` : ''}

            ${!staffFilter && revenueByStaff.length > 0 ? `
            <div class="section">
              <div class="section-title">${t('ownerRevenueByStaffMember')}</div>
              <table class="table">
                <tr>
                  <th>#</th>
                  <th>${t('ownerStaffLabelShort')}</th>
                  <th>${t('ownerRevenueEGP')}</th>
                </tr>
                ${revenueByStaff.map((s, i) => `
                  <tr>
                    <td>${i + 1}</td>
                    <td>${s.name}</td>
                    <td>${s.value === 0 ? 'EGP 0' : CURRENCY(s.value)}</td>
                  </tr>
                `).join('')}
              </table>
            </div>
            ` : ''}

            <div class="footer">
              <p>${t('ownerReportSummary')} - ${biz?.name || 'Business'}</p>
              <p>${t('ownerGeneratedOn')} ${new Date().toLocaleString('en-US')}</p>
            </div>
          </body>
        </html>
      `;

      // Open print dialog
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    };

    const zeroEverywhere = !filtered.length;

    // Show access code input if not validated
    if (!isPasscodeValidated) {
      return (
        <div className={`analytics-wrapper${lang === 'ar' ? ' rtl' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
          <div className="analytics-header">
            <div className="analytics-title-row">
              <h2 className="analytics-title">
                <span className="tab-title-icon-wrap analytics-title-icon-wrap" aria-hidden="true">
                  <FaChartBar className="analytics-title-icon" size={18} />
                </span>
                {t('ownerBusinessPerformanceOverview')}
              </h2>
            </div>
          </div>
          
          <div className="passcode-validation-container">
            <div className="passcode-lock-icon" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            
            <h3 className="passcode-access-title">{t('ownerAnalyticsAccessTitle')}</h3>
            <p className="passcode-access-hint">
              {t('ownerAnalyticsAccessHint')}
            </p>
            
            <div className="passcode-form">
              <input
                type="password"
                value={passcode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setPasscode(value);
                  setPasscodeError('');
                }}
                placeholder={t('ownerAnalyticsPasscodePlaceholder')}
                className="passcode-input"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    validatePasscode();
                  }
                }}
              />
              
              {passcodeError && (
                <p className="passcode-error">
                  {passcodeError}
                </p>
              )}
              
              <button
                type="button"
                onClick={validatePasscode}
                disabled={validatingPasscode || passcode.length !== 4}
                className={`passcode-submit ${passcode.length === 4 ? 'is-ready' : ''}`}
              >
                {validatingPasscode ? t('ownerValidating') : t('ownerAccessAnalytics')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`analytics-wrapper${lang === 'ar' ? ' rtl' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div className="analytics-header">
          <div className="analytics-title-row">
          <h2 className="analytics-title">
            <span className="tab-title-icon-wrap analytics-title-icon-wrap" aria-hidden="true">
              <FaChartBar className="analytics-title-icon" size={18} />
            </span>
            {t('ownerBusinessPerformanceOverview')}
          </h2>
          <button 
            className="lock-analytics-btn"
            onClick={() => setIsPasscodeValidated(false)}
          >
            🔒 {t('ownerLockAnalytics')}
          </button>
          </div>
          <div className="filters-inline">
            <div className="filters-inline-date-group">
              <div className="date-input-container">
                <input
                  type="date"
                  className={`filter-date ${!startDate ? 'empty' : 'filled'}`}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label={t('ownerStartDate')}
                />
                {!startDate && (
                  <div className="date-placeholder-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>{t('ownerStartDate')}</span>
                  </div>
                )}
              </div>
              <span className="date-sep">→</span>
              <div className="date-input-container">
                <input
                  type="date"
                  className={`filter-date ${!endDate ? 'empty' : 'filled'}`}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label={t('ownerEndDate')}
                />
                {!endDate && (
                  <div className="date-placeholder-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>{t('ownerEndDate')}</span>
                  </div>
                )}
              </div>
            </div>
            {(startDate || endDate) && (
              <button className='mini clear' onClick={() => { setStartDate(''); setEndDate(''); }}>{t('ownerReset')}</button>
            )}

            <div className="filters-inline-selects-row">
              <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)}>
                <option value="">{t('ownerAllStaff')}</option>
                {staffList.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}>
                <option value="">{t('ownerAllServices')}</option>
                {serviceList.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="range-select" value={range} onChange={e => setRange(e.target.value)} disabled={!!(startDate || endDate)}>
                <option value="day">{t('ownerLast30Days')}</option>
                <option value="week">{t('ownerLast12Weeks')}</option>
                <option value="month">{t('ownerLast12Months')}</option>
                <option value="year">{t('ownerLast5Years')}</option>
              </select>
            </div>

            <div className="filters-inline-actions">
              <button className="export-btn" onClick={exportCSV}>{t('ownerExportCSV')}</button>
              <button
                className="generate-report-btn"
                onClick={() => setShowReportSummary(true)}
              >
                {t('ownerGenerateReport')}
              </button>
            </div>
          </div>
        </div>

        {/* Report Summary Section */}
        {showReportSummary && (
          <div className={`report-summary-section${lang === 'ar' ? ' rtl' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'} style={{
            marginTop: '24px',
            marginBottom: '24px',
            marginHorizontal: '8px',
            ...(lang === 'ar' ? { direction: 'rtl', textAlign: 'right' } : {})
          }}>
            {/* Report Header */}
            <div className="report-header" style={{
              backgroundColor: '#18442A',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              padding: '20px',
              borderBottom: '4px solid #ffaa06'
            }}>
              {/* Title – "إنشاء التقرير" in AR, centered */}
              <h2 style={{
                fontSize: '24px',
                fontWeight: '900',
                color: '#ffffff',
                letterSpacing: '0.5px',
                marginBottom: '12px',
                textAlign: 'center'
              }}>{lang === 'ar' ? t('ownerGenerateReport') : t('ownerReportSummary')}</h2>
              
              {/* Date, Rating Label, and Stars */}
              <div
                className="report-header-meta"
                style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
                marginBottom: '12px',
                ...(lang === 'ar' ? { justifyContent: 'center', textAlign: 'center' } : {})
              }}>
                {(() => {
                  const completionRate = kpis.total > 0 ? (kpis.completed / kpis.total * 100) : 0;
                  const cancellationRate = kpis.total > 0 ? ((kpis.cancelled + kpis.declined) / kpis.total * 100) : 0;
                  let ratingLabel = t('ownerBad');
                  let starsText = '★☆☆☆☆';
                  
                  if (completionRate >= 90 && cancellationRate < 10) {
                    ratingLabel = t('ownerExcellent');
                    starsText = '★★★★★';
                  } else if (completionRate >= 75 && cancellationRate < 15) {
                    ratingLabel = t('ownerVeryGood');
                    starsText = '★★★★☆';
                  } else if (completionRate >= 60 && cancellationRate < 25) {
                    ratingLabel = t('ownerGood');
                    starsText = '★★★☆☆';
                  }
                  
                  return (
                    <>
                      <span className="report-header-shop-date" style={{
                        fontSize: '13px',
                        color: '#E4DBC4',
                        fontWeight: '500',
                        ...(lang === 'ar' ? { textAlign: 'center' } : {})
                      }}>
                        {biz?.name || 'Business'} • {new Date().toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                      <div className="report-header-rating-badge">
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '800',
                          color: '#18442A',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          ...(lang === 'ar' ? { textAlign: 'center' } : {})
                        }}>{ratingLabel}</span>
                        <span style={{
                          fontSize: '20px',
                          letterSpacing: '4px',
                          color: '#ffaa06',
                          fontWeight: '900',
                          whiteSpace: 'nowrap',
                          ...(lang === 'ar' ? { textAlign: 'center', direction: 'rtl' } : {})
                        }}>
                          {starsText}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
              
              {/* Rating Message */}
              <p style={{
                fontSize: '13px',
                color: '#E4DBC4',
                lineHeight: '20px',
                fontStyle: 'italic',
                marginTop: '0',
                ...(lang === 'ar' ? { textAlign: 'center' } : {})
              }}>
                {(() => {
                  const completionRate = kpis.total > 0 ? (kpis.completed / kpis.total * 100) : 0;
                  const cancellationRate = kpis.total > 0 ? ((kpis.cancelled + kpis.declined) / kpis.total * 100) : 0;
                  const pendingRate = kpis.total > 0 ? (kpis.pending / kpis.total * 100) : 0;
                  
                  if (completionRate >= 90 && cancellationRate < 10) {
                    return lang === 'ar' 
                      ? `أداء ممتاز! نسبة الإكمال ${completionRate.toFixed(1)}% ونسبة الإلغاء ${cancellationRate.toFixed(1)}% تشير إلى عمليات سلسة. استمر في هذا الأداء المتميز.`
                      : `Excellent performance! A ${completionRate.toFixed(1)}% completion rate and ${cancellationRate.toFixed(1)}% cancellation rate indicate smooth operations. Keep up this outstanding work.`;
                  } else if (completionRate >= 75 && cancellationRate < 15) {
                    return lang === 'ar'
                      ? `أداء جيد جداً. نسبة الإكمال ${completionRate.toFixed(1)}% قوية. تقليل الإلغاءات من ${cancellationRate.toFixed(1)}% سيعزز الربحية.`
                      : `Very good performance. Your ${completionRate.toFixed(1)}% completion rate is strong. Reducing cancellations from ${cancellationRate.toFixed(1)}% will boost profitability.`;
                  } else if (completionRate >= 60 && cancellationRate < 25) {
                    return lang === 'ar'
                      ? `أداء جيد. نسبة الإكمال ${completionRate.toFixed(1)}% قابلة للتحسين. ركز على تقليل الإلغاءات (${cancellationRate.toFixed(1)}%) وتحسين معدل الإكمال.`
                      : `Good performance. Your ${completionRate.toFixed(1)}% completion rate has room for improvement. Focus on reducing cancellations (${cancellationRate.toFixed(1)}%) and improving completion rates.`;
                  } else {
                    return lang === 'ar'
                      ? `يحتاج الأداء إلى تحسين. نسبة الإكمال ${completionRate.toFixed(1)}% ونسبة الإلغاء ${cancellationRate.toFixed(1)}% تتطلب اهتماماً فورياً. فكر في تحسين عملية الحجز والتواصل مع العملاء.`
                      : `Performance needs improvement. A ${completionRate.toFixed(1)}% completion rate and ${cancellationRate.toFixed(1)}% cancellation rate require immediate attention. Consider improving booking processes and customer communication.`;
                  }
                })()}
              </p>
              
              {/* Ranking Section */}
              {((staffFilter && (staffBookingsRank || staffRevenueRank)) || (serviceFilter && (serviceBookingsRank || serviceRevenueRank))) ? (
                    <div style={{
                      backgroundColor: '#f0f5f2',
                      borderLeft: lang === 'ar' ? 'none' : '4px solid #18442A',
                      borderRight: lang === 'ar' ? '4px solid #18442A' : 'none',
                      borderRadius: '8px',
                      padding: '16px',
                      marginTop: '20px',
                      marginBottom: '16px',
                      boxShadow: '0 2px 4px rgba(24,68,42,0.15)'
                    }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '800',
                        color: '#18442A',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        ...(lang === 'ar' ? { textAlign: 'right' } : {})
                      }}>{t('ownerRanking')}</h4>
                      <div style={{
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap',
                        ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                      }}>
                        {staffFilter && (staffBookingsRank || staffRevenueRank) && (
                          <div>
                            <div style={{
                              fontSize: '15px',
                              fontWeight: '700',
                              color: '#18442A',
                              marginBottom: '4px',
                              ...(lang === 'ar' ? { textAlign: 'right' } : {})
                            }}>{staffFilter}</div>
                            <div style={{
                              display: 'flex',
                              gap: '8px',
                              flexWrap: 'wrap',
                              ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                            }}>
                              {staffBookingsRank && (
                                <span style={{
                                  backgroundColor: '#18442A',
                                  color: '#ffffff',
                                  padding: '8px 14px',
                                  borderRadius: '16px',
                                  fontSize: '13px',
                                  fontWeight: '800',
                                  letterSpacing: '0.5px',
                                  border: '2px solid #2563eb',
                                  boxShadow: '0 2px 3px rgba(0,0,0,0.15)'
                                }}>#{staffBookingsRank} {t('ownerBookingsRank')}</span>
                              )}
                              {staffRevenueRank && (
                                <span style={{
                                  backgroundColor: '#18442A',
                                  color: '#ffffff',
                                  padding: '8px 14px',
                                  borderRadius: '16px',
                                  fontSize: '13px',
                                  fontWeight: '800',
                                  letterSpacing: '0.5px',
                                  border: '2px solid #18442A',
                                  boxShadow: '0 2px 3px rgba(0,0,0,0.15)'
                                }}>#{staffRevenueRank} {t('ownerRevenueRank')}</span>
                              )}
                            </div>
                          </div>
                        )}
                        {serviceFilter && (serviceBookingsRank || serviceRevenueRank) && (
                          <div>
                            <div style={{
                              fontSize: '15px',
                              fontWeight: '700',
                              color: '#18442A',
                              marginBottom: '4px',
                              ...(lang === 'ar' ? { textAlign: 'right' } : {})
                            }}>{serviceFilter}</div>
                            <div style={{
                              display: 'flex',
                              gap: '8px',
                              flexWrap: 'wrap',
                              ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                            }}>
                              {serviceBookingsRank && (
                                <span style={{
                                  backgroundColor: '#18442A',
                                  color: '#ffffff',
                                  padding: '8px 14px',
                                  borderRadius: '16px',
                                  fontSize: '13px',
                                  fontWeight: '800',
                                  letterSpacing: '0.5px',
                                  border: '2px solid #2563eb',
                                  boxShadow: '0 2px 3px rgba(0,0,0,0.15)'
                                }}>#{serviceBookingsRank} {t('ownerBookingsRank')}</span>
                              )}
                              {serviceRevenueRank && (
                                <span style={{
                                  backgroundColor: '#18442A',
                                  color: '#ffffff',
                                  padding: '8px 14px',
                                  borderRadius: '16px',
                                  fontSize: '13px',
                                  fontWeight: '800',
                                  letterSpacing: '0.5px',
                                  border: '2px solid #18442A',
                                  boxShadow: '0 2px 3px rgba(0,0,0,0.15)'
                                }}>#{serviceRevenueRank} {t('ownerRevenueRank')}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
              ) : null}
            </div>
            
            {/* Report Document Body */}
            <div className="report-document" style={{
              backgroundColor: '#F3EDE3',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
              padding: '20px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}>
              {/* Metadata */}
              <div style={{
                backgroundColor: '#F3EDE3',
                padding: '16px',
                borderRadius: '6px',
                marginBottom: '24px'
              }}>
                <div className="report-label-value-row report-meta-row" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                }}>
                  {lang === 'ar' ? (
                    <>
                      <span style={{ color: '#18442A', textAlign: 'right' }}>
                        {startDate || endDate 
                          ? `${startDate || t('ownerStart')} ${lang === 'ar' ? '←' : '→'} ${endDate || t('ownerEnd')}`
                          : (range === 'day' ? 'اليوم' : range === 'week' ? 'هذا الأسبوع' : range === 'month' ? 'هذا الشهر' : 'هذه السنة')}
                      </span>
                      <span style={{ fontWeight: '600', color: '#5c6b60', textAlign: 'right' }}>{t('ownerDateRange')}:</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontWeight: '600', color: '#5c6b60' }}>{t('ownerDateRange')}:</span>
                      <span style={{ color: '#18442A' }}>
                        {startDate || endDate 
                          ? `${startDate || t('ownerStart')} → ${endDate || t('ownerEnd')}`
                          : (range === 'day' ? 'Today' : range === 'week' ? 'This Week' : range === 'month' ? 'This Month' : 'This Year')}
                      </span>
                    </>
                  )}
                </div>
                <div className="report-label-value-row report-meta-row" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '12px',
                  ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                }}>
                  {lang === 'ar' ? (
                    <>
                      <span style={{ color: '#18442A', textAlign: 'right' }}>{staffFilter || t('ownerAllStaff')}</span>
                      <span style={{ fontWeight: '600', color: '#5c6b60', textAlign: 'right' }}>{t('ownerStaffLabelShort')}:</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontWeight: '600', color: '#5c6b60' }}>{t('ownerStaffLabelShort')}:</span>
                      <span style={{ color: '#18442A' }}>{staffFilter || t('ownerAllStaff')}</span>
                    </>
                  )}
                </div>
                <div className="report-label-value-row report-meta-row" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                }}>
                  {lang === 'ar' ? (
                    <>
                      <span style={{ color: '#18442A', textAlign: 'right' }}>{serviceFilter || t('ownerAllServices')}</span>
                      <span style={{ fontWeight: '600', color: '#5c6b60', textAlign: 'right' }}>{t('ownerServiceLabelShort')}:</span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontWeight: '600', color: '#5c6b60' }}>{t('ownerServiceLabelShort')}:</span>
                      <span style={{ color: '#18442A' }}>{serviceFilter || t('ownerAllServices')}</span>
                    </>
                  )}
                </div>
              </div>
              
              {/* Executive Summary */}
              <div style={{ marginBottom: '28px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '800',
                  color: '#18442A',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...(lang === 'ar' ? { textAlign: 'right' } : {})
                }}>{t('ownerAnalyticsTitle')}</h3>
                <div style={{
                  border: 'none',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  {[
                    { label: t('ownerTotalAppointments'), value: kpis.total },
                    { label: t('dashStatCompleted'), value: kpis.completed, color: '#18442A' },
                    { label: t('dashStatPending'), value: kpis.pending, color: '#FFA500' },
                    { label: t('ownerAccepted'), value: kpis.accepted, color: '#18442A' },
                    { label: t('ownerCancellationRate'), value: `${((kpis.cancelled + kpis.declined) / (kpis.total || 1) * 100).toFixed(1)}%`, color: '#F44336' },
                    { label: t('ownerRevenueEGP'), value: kpis.revenue === 0 ? 'EGP 0' : CURRENCY(kpis.revenue) },
                    ...(lang !== 'ar' ? [
                      { label: t('ownerAvgBookingValue'), value: kpis.avgBookingValue === 0 ? 'EGP 0' : CURRENCY(kpis.avgBookingValue) },
                      { label: t('ownerCompletionRate'), value: `${kpis.completionRate.toFixed(1)}%` }
                    ] : [])
                  ].map((item, i) => (
                    <div key={i} className="report-label-value-row report-exec-row" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      borderBottom: i < 7 ? '1px solid #e5e7eb' : 'none',
                      ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                    }}>
                      {lang === 'ar' ? (
                        <>
                          <span style={{
                            fontWeight: '700',
                            color: item.color || '#1f2937',
                            textAlign: 'right'
                          }}>{item.value}</span>
                          <span style={{
                            fontWeight: '600',
                            color: '#5c6b60',
                            textAlign: 'right'
                          }}>{item.label}:</span>
                        </>
                      ) : (
                        <>
                          <span style={{
                            fontWeight: '600',
                            color: '#5c6b60'
                          }}>{item.label}:</span>
                          <span style={{
                            fontWeight: '700',
                            color: item.color || '#1f2937'
                          }}>{item.value}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Top Services by Bookings - Only if NOT filtering by service */}
              {!serviceFilter && topServices.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '800',
                    color: '#18442A',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    ...(lang === 'ar' ? { textAlign: 'right' } : {})
                  }}>{t('ownerTopServicesByBookings')}</h3>
                  <div style={{
                    border: 'none',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {topServices.map((s, i) => (
                      <div key={i} className="report-numbered-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: i < topServices.length - 1 ? '1px solid #e5e7eb' : 'none',
                        ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                      }}>
                        {lang === 'ar' ? (
                          <>
                            <span style={{
                              fontWeight: '700',
                              color: '#18442A',
                              textAlign: 'right'
                            }}>{s.value}</span>
                            <span style={{
                              fontWeight: '600',
                              color: '#18442A',
                              textAlign: 'right'
                            }}>{s.name}</span>
                            <span style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '14px',
                              backgroundColor: '#18442A',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '800'
                            }}>{i + 1}</span>
                          </>
                        ) : (
                          <>
                            <div className="report-numbered-inner" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <span style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '14px',
                                backgroundColor: '#18442A',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: '800'
                              }}>{i + 1}</span>
                              <span style={{
                                fontWeight: '600',
                                color: '#18442A'
                              }}>{s.name}</span>
                            </div>
                            <span style={{
                              fontWeight: '700',
                              color: '#18442A'
                            }}>{s.value}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Top Services by Revenue - Only if NOT filtering by service */}
              {!serviceFilter && topRevenueServices.length > 0 ? (
                <div style={{ marginBottom: '28px' }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '800',
                      color: '#18442A',
                      marginBottom: '16px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      ...(lang === 'ar' ? { textAlign: 'right' } : {})
                    }}>{t('ownerTopServicesByRevenue')}</h3>
                    <div style={{
                      border: 'none',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      {topRevenueServices.map((s, i) => (
                        <div key={i} className="report-numbered-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px',
                          borderBottom: i < topRevenueServices.length - 1 ? '1px solid #e5e7eb' : 'none',
                          ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                        }}>
                          {lang === 'ar' ? (
                            <>
                              <span style={{
                                fontWeight: '700',
                                color: '#18442A',
                                textAlign: 'right'
                              }}>{s.value === 0 ? 'EGP 0' : CURRENCY(s.value)}</span>
                              <span style={{
                                fontWeight: '600',
                                color: '#18442A',
                                textAlign: 'right'
                              }}>{s.name}</span>
                              <span style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '14px',
                                backgroundColor: '#18442A',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: '800'
                              }}>{i + 1}</span>
                            </>
                          ) : (
                            <>
                              <div className="report-numbered-inner" style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}>
                                <span style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '14px',
                                  backgroundColor: '#18442A',
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: '800'
                                }}>{i + 1}</span>
                                <span style={{
                                  fontWeight: '600',
                                  color: '#18442A'
                                }}>{s.name}</span>
                              </div>
                              <span style={{
                                fontWeight: '700',
                                color: '#18442A'
                              }}>{s.value === 0 ? 'EGP 0' : CURRENCY(s.value)}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
              ) : null}
              
              {/* Bookings by Staff - Only if NOT filtering by staff */}
              {!staffFilter && bookingsByStaff.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '800',
                    color: '#18442A',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    ...(lang === 'ar' ? { textAlign: 'right' } : {})
                  }}>{t('ownerBookingsByStaffMember')}</h3>
                  <div style={{
                    border: 'none',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {bookingsByStaff.map((s, i) => (
                      <div key={i} className="report-numbered-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: i < bookingsByStaff.length - 1 ? '1px solid #e5e7eb' : 'none',
                        ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                      }}>
                        {lang === 'ar' ? (
                          <>
                            <span style={{
                              fontWeight: '700',
                              color: '#18442A',
                              textAlign: 'right'
                            }}>{s.value}</span>
                            <span style={{
                              fontWeight: '600',
                              color: '#18442A',
                              textAlign: 'right'
                            }}>{s.name}</span>
                            <span style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '14px',
                              backgroundColor: '#18442A',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '800'
                            }}>{i + 1}</span>
                          </>
                        ) : (
                          <>
                            <div className="report-numbered-inner" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <span style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '14px',
                                backgroundColor: '#18442A',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: '800'
                              }}>{i + 1}</span>
                              <span style={{
                                fontWeight: '600',
                                color: '#18442A'
                              }}>{s.name}</span>
                            </div>
                            <span style={{
                              fontWeight: '700',
                              color: '#18442A'
                            }}>{s.value}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Revenue by Staff - Only if NOT filtering by staff */}
              {!staffFilter && revenueByStaff.length > 0 && (
                <div style={{ marginBottom: '28px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '800',
                    color: '#18442A',
                    marginBottom: '16px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    ...(lang === 'ar' ? { textAlign: 'right' } : {})
                  }}>{t('ownerRevenueByStaffMember')}</h3>
                  <div style={{
                    border: 'none',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    {revenueByStaff.map((s, i) => (
                      <div key={i} className="report-numbered-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: i < revenueByStaff.length - 1 ? '1px solid #e5e7eb' : 'none',
                        ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                      }}>
                        {lang === 'ar' ? (
                          <>
                            <span style={{
                              fontWeight: '700',
                              color: '#18442A',
                              textAlign: 'right'
                            }}>{s.value === 0 ? 'EGP 0' : CURRENCY(s.value)}</span>
                            <span style={{
                              fontWeight: '600',
                              color: '#18442A',
                              textAlign: 'right'
                            }}>{s.name}</span>
                            <span style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '14px',
                              backgroundColor: '#18442A',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '800'
                            }}>{i + 1}</span>
                          </>
                        ) : (
                          <>
                            <div className="report-numbered-inner" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <span style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '14px',
                                backgroundColor: '#18442A',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                fontWeight: '800'
                              }}>{i + 1}</span>
                              <span style={{
                                fontWeight: '600',
                                color: '#18442A'
                              }}>{s.name}</span>
                            </div>
                            <span style={{
                              fontWeight: '700',
                              color: '#18442A'
                            }}>{s.value === 0 ? 'EGP 0' : CURRENCY(s.value)}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Export PDF and Close – side by side on laptop, stacked on mobile */}
            <div className="report-summary-actions" style={{
              marginTop: '24px',
              paddingTop: '20px',
              borderTop: '1px solid #E4DBC4',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <button
                onClick={exportPDF}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#18442A',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 8px rgba(24, 68, 42, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0f2e1a'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#18442A'}
              >
                📄 {t('ownerExportPDF')}
              </button>
              <button
                onClick={() => setShowReportSummary(false)}
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#dc2626',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 8px rgba(220, 38, 38, 0.25)'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
              >
                {t('close')}
              </button>
            </div>
          </div>
        )}
        

        <div className="kpi-grid">
          <KpiTile label={t('ownerTotalAppointments')} value={kpis.total} accent="total" />
          <KpiTile label={t('dashStatCompleted')} value={kpis.completed} accent="completed" />
          <KpiTile label={t('ownerRevenueEGP')} value={CURRENCY(kpis.revenue)} accent="money" />
          <KpiTile label={t('ownerAvgBookingValue')} value={`${CURRENCY(kpis.avgBookingValue)} EGP`} accent="avg" />
          <KpiTile label={t('ownerCompletionRate')} value={`${kpis.completionRate.toFixed(1)}%`} accent="conv" />
          <KpiTile label={t('ownerCancellationRate')} value={`${kpis.cancellationRate.toFixed(1)}%`} accent="cancel" />
          <KpiTile label={t('dashStatPending')} value={kpis.pending} accent="pending" />
          <KpiTile label={t('ownerAccepted')} value={kpis.accepted} accent="accepted" />
        </div>

        {/* ─── Overview (Performance snapshot, Business health, Trend indicators, Quick insights, Performance alerts) ─── */}
        <div className="analytics-group chart-card chart-card--wide">
          <button type="button" className="analytics-group-toggle" onClick={() => setExpandedGroups(g => ({ ...g, overview: !g.overview }))} aria-expanded={expandedGroups.overview}>
            <span className="analytics-group-title">{lang === 'ar' ? 'نظرة عامة' : 'Overview'}</span>
            <span className="analytics-group-chevron">{expandedGroups.overview ? '\u25B2' : '\u25BC'}</span>
          </button>
          {expandedGroups.overview && (
            <div className="analytics-group-content">
              <div className="analytics-chart-wrap">
                <h4 className="analytics-subsection-title">{lang === 'ar' ? 'معدل الإكمال والإلغاء' : 'Completion vs cancellation'}</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={overviewHealthData} layout="vertical" margin={{ top: 8, right: 24, left: 80, bottom: 8 }}>
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: '#667085' }} />
                    <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 11, fill: '#667085' }} />
                    <Tooltip formatter={(v) => [`${v}%`, '']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="var(--primary)" name="" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="analytics-section-row">
                <div className="analytics-mini-card"><strong>{lang === 'ar' ? 'لقطة الأداء' : 'Performance snapshot'}</strong><span className="analytics-mini-value">{kpis.completed}</span><p>{t('dashStatCompleted')}, {CURRENCY(kpis.revenue)} EGP</p></div>
                <div className="analytics-mini-card"><strong>{lang === 'ar' ? 'صحة الأعمال' : 'Business health'}</strong><span className="analytics-mini-value">{kpis.completionRate >= 70 ? (lang === 'ar' ? 'جيد' : 'Good') : (lang === 'ar' ? 'تحسين' : 'Improve')}</span><p>{kpis.completionRate.toFixed(0)}% {lang === 'ar' ? 'إكمال' : 'completion'}</p></div>
                <div className="analytics-mini-card"><strong>{lang === 'ar' ? 'الاتجاه' : 'Trend'}</strong><span className="analytics-mini-value">{kpis.completionRate >= 85 ? (lang === 'ar' ? 'إيجابي' : 'Positive') : (lang === 'ar' ? 'مستقر' : 'Stable')}</span><p>{filtered.length} {lang === 'ar' ? 'حجز' : 'bookings'}</p></div>
                <div className="analytics-mini-card"><strong>{lang === 'ar' ? 'تنبيهات' : 'Alerts'}</strong><span className="analytics-mini-value">{kpis.cancellationRate > 15 ? '!' : '—'}</span><p>{kpis.cancellationRate > 15 ? (lang === 'ar' ? 'معدل إلغاء مرتفع' : 'High cancellation') : (lang === 'ar' ? 'لا تنبيهات' : 'No alerts')}</p></div>
              </div>
            </div>
          )}
        </div>

        {/* ─── Bookings (Conversion funnel, Booking trends, Peak hours analysis) ─── */}
        <div className="analytics-group chart-card chart-card--wide">
          <button type="button" className="analytics-group-toggle" onClick={() => setExpandedGroups(g => ({ ...g, bookings: !g.bookings }))} aria-expanded={expandedGroups.bookings}>
            <span className="analytics-group-title">{lang === 'ar' ? 'الحجوزات' : 'Bookings'}</span>
            <span className="analytics-group-chevron">{expandedGroups.bookings ? '\u25B2' : '\u25BC'}</span>
          </button>
          {expandedGroups.bookings && (
            <div className="analytics-group-content">
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'قمع التحويل' : 'Conversion funnel'}</h4>
              {conversionFunnelData.length > 0 ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={conversionFunnelData} margin={{ top: 12, right: 20, left: 12, bottom: 60 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#667085' }} angle={-25} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11, fill: '#667085' }} />
                      <Tooltip formatter={(v) => [v, '']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={true}>
                        {conversionFunnelData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{t('dashStatPending')}: {kpis.pending} → {t('ownerAccepted')}: {kpis.accepted} → {t('dashStatCompleted')}: {kpis.completed}</p>}
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'ساعات الذروة (إيرادات)' : 'Peak hours (revenue)'}</h4>
              {peakHours.some(h => h.revenue > 0) ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={peakHours} margin={{ top: 8, right: 16, left: 8, bottom: 50 }}>
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#667085' }} interval="preserveStartEnd" />
                      <YAxis tickFormatter={(v) => (v / 1000).toFixed(0) + 'k'} tick={{ fontSize: 10, fill: '#667085' }} />
                      <Tooltip formatter={(v) => [CURRENCY(v) + ' EGP', 'Revenue']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Bar dataKey="revenue" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{peakHours.filter(h => h.revenue > 0).length} {lang === 'ar' ? 'ساعات بها إيرادات' : 'hours with revenue'}</p>}
            </div>
          )}
        </div>

        {/* ─── Feedback (Shop rating, Rating distribution, Customer reviews) ─── */}
        <div className="analytics-group chart-card chart-card--wide">
          <button type="button" className="analytics-group-toggle" onClick={() => setExpandedGroups(g => ({ ...g, feedback: !g.feedback }))} aria-expanded={expandedGroups.feedback}>
            <span className="analytics-group-title">{lang === 'ar' ? 'الملاحظات والتقييمات' : 'Feedback'}</span>
            <span className="analytics-group-chevron">{expandedGroups.feedback ? '\u25B2' : '\u25BC'}</span>
          </button>
          {expandedGroups.feedback && (
            <div className="analytics-group-content">
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'تقييم المتجر' : 'Shop rating'}</h4>
              <div className="star-rating-block">
                <span className="star-big">{(biz?.rating || 0).toFixed(1)}</span>
                <div>
                  <span className="stars-visual" aria-hidden="true">{[1,2,3,4,5].map(s => (s <= Math.round(biz?.rating || 0) ? '★' : '☆')).join('')}</span>
                  <p className="star-label">{biz?.reviews ?? reviews.length} {t('ownerTotalReviews')}</p>
                </div>
              </div>
              {ratingDistribution.some(r => r.value > 0) && (
                <>
                  <h4 className="analytics-subsection-title">{lang === 'ar' ? 'توزيع التقييمات' : 'Rating distribution'}</h4>
                  <div className="analytics-chart-wrap">
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={ratingDistribution} margin={{ top: 8, right: 16, left: 8, bottom: 40 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#667085' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#667085' }} />
                        <Tooltip formatter={(v) => [v, t('ownerTotalReviews')]} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                        <Bar dataKey="value" fill="#FFB800" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Ratings & Reviews Section */}
        <div className="chart-card chart-card--wide">
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#18442A', ...(lang === 'ar' ? { textAlign: 'right' } : {}) }}>
            {t('ownerRatingsReviews')}
          </h3>
          
          {loadingReviews ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#5c6b60' }}>Loading reviews...</div>
          ) : (
            <>
              {(biz?.rating > 0 || reviews.length > 0) ? (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '20px',
                  backgroundColor: '#F3EDE3',
                  borderRadius: '12px',
                  marginBottom: '16px',
                  ...(lang === 'ar' ? { direction: 'rtl' } : {})
                }}>
                  <div style={{ fontSize: '48px', fontWeight: '800', color: '#18442A', marginBottom: '8px' }}>
                    {(biz?.rating || 0).toFixed(1)}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span key={star} style={{ fontSize: '20px', color: star <= Math.round(biz?.rating || 0) ? '#FFB800' : '#d1d5db' }}>★</span>
                    ))}
                  </div>
                  <div style={{ fontSize: '14px', color: '#5c6b60' }}>
                    {biz?.reviews || reviews.length} {t('ownerTotalReviews')}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#5c6b60' }}>
                  {t('ownerNoReviewsYet')}
                </div>
              )}
              
              {reviews.length > 0 && (
                <>
                  <button
                    onClick={() => setShowReviews(!showReviews)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#E8EDE8',
                      border: '1px solid #18442A',
                      borderRadius: '12px',
                      color: '#18442A',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {})
                    }}
                  >
                    {showReviews ? t('ownerHideReviews') : t('ownerShowReviews')}
                    {showReviews ? '↑' : '↓'}
                  </button>
                  
                  {showReviews && (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {reviews.slice(0, 10).map((review, index) => (
                        <div key={review._id || index} style={{
                          padding: '16px',
                          marginBottom: '12px',
                          backgroundColor: '#F3EDE3',
                          borderRadius: '12px',
                          border: 'none',
                          ...(lang === 'ar' ? { direction: 'rtl', textAlign: 'right' } : {})
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', ...(lang === 'ar' ? { flexDirection: 'row-reverse' } : {}) }}>
                            <div style={{ fontWeight: '700', color: '#18442A' }}>{review.userName || review.user?.name || 'Customer'}</div>
                            <div style={{ display: 'flex', gap: '2px' }}>
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span key={star} style={{ fontSize: '14px', color: star <= review.rating ? '#FFB800' : '#d1d5db' }}>★</span>
                              ))}
                            </div>
                          </div>
                          {review.comment && (
                            <div style={{ color: '#4b5563', marginBottom: '8px', lineHeight: '1.5' }}>{review.comment}</div>
                          )}
                          {review.createdAt && (
                            <div style={{ fontSize: '12px', color: '#5c6b60' }}>
                              {new Date(review.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Chart 1 - Main Performance */}
        <div className="chart-card chart-card--wide">
            <h3>{rangeLabel} – {t('ownerBookingsVsRevenue')}</h3>
            {lineData?.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={lineData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#18442A" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#18442A" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF6B6B" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#FF6B6B" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                  <XAxis 
                    dataKey="label" 
                    interval="preserveStartEnd" 
                    tick={{ fontSize: 12, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                  />
                  <YAxis 
                    yAxisId="left" 
                    tick={{ fontSize: 12, fill: '#667085', fontWeight: 500 }} 
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    label={{ value: 'Bookings', angle: -90, position: 'insideLeft', dy: -8, style: { textAnchor: 'middle', fill: '#667085', fontWeight: 600 } }} 
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    tickFormatter={(v) => `${(v/1000).toFixed(1)}k`} 
                    tick={{ fontSize: 12, fill: '#667085', fontWeight: 500 }} 
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    label={{ value: 'Revenue (EGP)', angle: -90, position: 'insideRight', dy: 10, style: { textAnchor: 'middle', fill: '#667085', fontWeight: 600 } }} 
                  />
                  <Tooltip 
                    formatter={(v, k) => [
                      k === 'revenue' ? `${Number(v).toLocaleString('en-US')} EGP` : `${v} bookings`,
                      k === 'revenue' ? 'Revenue' : 'Bookings'
                    ]}
                    labelFormatter={(label) => `Period: ${label}`}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#0e1726' }}
                    wrapperClassName="analytics-chart-tooltip"
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={40} 
                    wrapperStyle={{ paddingBottom: '20px' }}
                    iconType="rect"
                    iconSize={12}
                  />
                  <Bar 
                    yAxisId="left" 
                    dataKey="books" 
                    barSize={20} 
                    radius={[6, 6, 0, 0]} 
                    name="Bookings" 
                    fill="url(#bookingsGradient)"
                    opacity={0.9}
                  />
                  <Line 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="revenue" 
                    strokeWidth={4} 
                    dot={{ fill: '#FF6B6B', strokeWidth: 3, r: 6, stroke: '#fff' }} 
                    activeDot={{ r: 8, stroke: '#FF6B6B', strokeWidth: 2, fill: '#fff' }}
                    name="Revenue" 
                    stroke="url(#revenueGradient)"
                    strokeDasharray="0"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (<p className="placeholder">{zeroEverywhere ? 'No records found.' : 'No dated records for this period.'}</p>)}
          </div>

        {/* Chart 2 - Status Distribution */}
        <div className="chart-card chart-card--wide">
            <h3>{t('ownerAppointmentStatusDistribution')}</h3>
            {statusDistribution.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <defs>
                    <linearGradient id="pendingGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FF9800" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#FF9800" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="acceptedGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#18442A" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#18442A" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="declinedGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#F44336" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#F44336" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="cancelledGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#9C27B0" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#9C27B0" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="completedGradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#18442A" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#18442A" stopOpacity={0.4}/>
                    </linearGradient>
                  </defs>
                  <Tooltip 
                    formatter={(value, name) => [`${value} (${((value / statusDistribution.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%)`, name]}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#0e1726' }}
                    wrapperClassName="analytics-chart-tooltip"
                  />
                  <Pie 
                    data={statusDistribution} 
                    dataKey="value" 
                    nameKey="name" 
                    innerRadius={60} 
                    outerRadius={100} 
                    label={({ name, percent }) => percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                    labelLine={false}
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {statusDistribution.map((_, i) => {
                      const gradients = ['pendingGradient', 'acceptedGradient', 'declinedGradient', 'cancelledGradient', 'completedGradient'];
                      return (
                      <Cell 
                        key={i} 
                          fill={`url(#${gradients[i % gradients.length]})`}
                      />
                      );
                    })}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : <p className='placeholder'>{t('ownerNoAppointmentDataAvailable')}</p>}
          </div>

        {/* Chart 3 - Top Services (vertical bars) */}
        <div className="chart-card chart-card--wide">
            <h3>{t('ownerTopServicesByBookings')}</h3>
            {topServices.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={topServices} margin={{ top: 20, right: 30, left: 50, bottom: 80 }}>
                  <defs>
                    <linearGradient id="servicesGradient" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#18442A" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#18442A" stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                  <XAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 11, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    angle={-35}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis 
                    type="number"
                    tick={{ fontSize: 12, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickFormatter={(value) => value.toString()}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value} bookings`, 'Bookings']}
                    labelFormatter={(label) => `Service: ${label}`}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#0e1726' }}
                    wrapperClassName="analytics-chart-tooltip"
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[6, 6, 0, 0]} 
                    fill="url(#servicesGradient)"
                    opacity={0.9}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className='placeholder'>{t('ownerNoServiceBookingsAvailable')}</p>}
        </div>

        {/* Chart 4 - Revenue by Staff (vertical bars) */}
        <div className="chart-card chart-card--wide">
            <h3>{t('ownerRevenueByStaffMember')}</h3>
            {revenueByStaff.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={revenueByStaff} margin={{ top: 20, right: 30, left: 50, bottom: 80 }}>
                  <defs>
                    <linearGradient id="revenueStaffGradient" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#FF6B6B" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#FF6B6B" stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                  <XAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 11, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    angle={-35}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis 
                    type="number"
                    tickFormatter={(v) => `${(v/1000).toFixed(1)}k`}
                    tick={{ fontSize: 12, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                  />
                  <Tooltip 
                    formatter={(v) => [`${Number(v).toLocaleString('en-US')} EGP`, 'Revenue']}
                    labelFormatter={(label) => `Staff: ${label}`}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#0e1726' }}
                    wrapperClassName="analytics-chart-tooltip"
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[6, 6, 0, 0]} 
                    fill="url(#revenueStaffGradient)"
                    opacity={0.9}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className='placeholder'>{t('ownerNoStaffRevenueDataAvailable')}</p>}
        </div>

        {/* Chart 5 - Bookings by Staff (vertical bars) */}
        <div className="chart-card chart-card--wide">
            <h3>{t('ownerBookingsByStaffMember')}</h3>
            {bookingsByStaff.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={bookingsByStaff} margin={{ top: 20, right: 30, left: 50, bottom: 80 }}>
                  <defs>
                    <linearGradient id="bookingsStaffGradient" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#4CAF50" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#4CAF50" stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                  <XAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 11, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    angle={-35}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis 
                    type="number"
                    tick={{ fontSize: 12, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickFormatter={(value) => value.toString()}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value} bookings`, 'Bookings']}
                    labelFormatter={(label) => `Staff: ${label}`}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#0e1726' }}
                    wrapperClassName="analytics-chart-tooltip"
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[6, 6, 0, 0]} 
                    fill="url(#bookingsStaffGradient)"
                    opacity={0.9}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className='placeholder'>{t('ownerNoStaffBookingDataAvailable')}</p>}
        </div>

        {/* Chart 6 - Revenue by Service Type */}
        <div className="chart-card chart-card--wide">
            <h3>{t('ownerRevenueByServiceType')}</h3>
            {revenueByService.length ? (
              <ResponsiveContainer width="100%" height={360}>
                <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <defs>
                    <linearGradient id="service1Gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#18442A" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#18442A" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="service2Gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#4CAF50" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#4CAF50" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="service3Gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FF9800" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#FF9800" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="service4Gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#F44336" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#F44336" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="service5Gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#9C27B0" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#9C27B0" stopOpacity={0.4}/>
                    </linearGradient>
                    <linearGradient id="service6Gradient" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#2196F3" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#2196F3" stopOpacity={0.4}/>
                    </linearGradient>
                  </defs>
                  <Tooltip 
                    formatter={(value, name) => [
                      `${Number(value).toLocaleString('en-US')} EGP (${((value / revenueByService.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%)`, 
                      name
                    ]}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#0e1726' }}
                    wrapperClassName="analytics-chart-tooltip"
                  />
                  <Pie 
                    data={revenueByService} 
                    dataKey="value" 
                    nameKey="name" 
                    innerRadius={50} 
                    outerRadius={100} 
                    label={({ name, percent }) => percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                    labelLine={false}
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {revenueByService.map((_, i) => {
                      const gradients = ['service1Gradient', 'service2Gradient', 'service3Gradient', 'service4Gradient', 'service5Gradient', 'service6Gradient'];
                      return (
                      <Cell 
                        key={i} 
                          fill={`url(#${gradients[i % gradients.length]})`}
                      />
                      );
                    })}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            ) : <p className='placeholder'>{t('ownerNoServiceRevenueDataAvailable')}</p>}
        </div>

        {/* Chart 7 - Staff Performance Overview */}
        <div className="chart-card chart-card--wide">
            <h3>{t('ownerStaffPerformanceOverview')}</h3>
            {bookingsByStaff.length ? (
              <div className="staff-performance-list">
                {bookingsByStaff.slice(0, 6).map((staff, index) => {
                  const staffRevenue = revenueByStaff.find(r => r.name === staff.name);
                  const completionRate = filtered.filter(a => a.staff === staff.name && a.status === 'completed').length / 
                                       filtered.filter(a => a.staff === staff.name).length * 100;
                  
                  return (
                    <div key={staff.name} className="staff-performance-item">
                      <div className="staff-header">
                        <span className="staff-name">{staff.name}</span>
                        <span className="staff-rank">#{index + 1}</span>
                      </div>
                      <div className="staff-metrics">
                        <div className="metric">
                          <span className="metric-label">{t('ownerBookings')}</span>
                          <span className="metric-value">{staff.value}</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">{t('ownerRevenue')}</span>
                          <span className="metric-value">{CURRENCY(staffRevenue?.value || 0)} EGP</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">{t('ownerCompletion')}</span>
                          <span className="metric-value">{completionRate.toFixed(1)}%</span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">{t('ownerAvgValue')}</span>
                          <span className="metric-value">
                            {staffRevenue?.value && staff.value 
                              ? `${CURRENCY(Math.round(staffRevenue.value / staff.value))} EGP`
                              : 'N/A'
                            }
                          </span>
                        </div>
                      </div>
                      <div className="staff-progress">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${completionRate}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className='placeholder'>{t('ownerNoStaffDataAvailable')}</p>}
          </div>

          <div className="chart-card">
            <h3>{t('ownerServicePerformanceOverview')}</h3>
            {servicePerformance.length ? (
              <div className="service-performance-list">
                {servicePerformance.slice(0, 5).map((service, index) => (
                  <div key={service.name} className="service-performance-item">
                    <div className="service-header">
                      <span className="service-name">{service.name}</span>
                      <span className="service-rank">#{index + 1}</span>
                    </div>
                    <div className="service-metrics">
                      <div className="metric">
                        <span className="metric-label">{t('ownerRevenue')}</span>
                        <span className="metric-value">{CURRENCY(service.totalRevenue)} EGP</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">{t('ownerBookings')}</span>
                        <span className="metric-value">{service.totalBookings}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">{t('ownerCompletion')}</span>
                        <span className="metric-value">{service.completionRate.toFixed(1)}%</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">{t('ownerAvgPrice')}</span>
                        <span className="metric-value">{CURRENCY(service.avgPrice)} EGP</span>
                      </div>
                    </div>
                    <div className="service-progress">
                      <div 
                        className="progress-bar" 
                        style={{ width: `${service.completionRate}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className='placeholder'>{t('ownerNoServicePerformanceDataAvailable')}</p>}
        </div>

        {/* Chart 8 - Revenue by Hour */}
        <div className="chart-card chart-card--wide">
            <h3>{t('ownerRevenueByHour')}</h3>
            {peakHours.some(h => h.revenue > 0) ? (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={peakHours} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="revenueHoursGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#18442A" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#18442A" stopOpacity={0.3}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.06)" />
                  <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 11, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#667085', fontWeight: 500 }}
                    axisLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickLine={{ stroke: 'rgba(0,0,0,0.1)' }}
                    tickFormatter={(value) => `${(value/1000).toFixed(1)}k`}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${CURRENCY(value)} EGP`, 'Revenue']}
                    labelFormatter={(label) => `Time: ${label}`}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 600, color: '#0e1726' }}
                    wrapperClassName="analytics-chart-tooltip"
                  />
                  <Bar 
                    dataKey="revenue" 
                    radius={[4, 4, 0, 0]} 
                    fill="url(#revenueHoursGradient)"
                    opacity={0.9}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className='placeholder'>{t('ownerNoRevenueByHourDataAvailable')}</p>}
        </div>

        {/* ─── Services (Service mix, Service cancellation rate, etc.) ─── */}
        <div className="analytics-group chart-card chart-card--wide">
          <button type="button" className="analytics-group-toggle" onClick={() => setExpandedGroups(g => ({ ...g, services: !g.services }))} aria-expanded={expandedGroups.services}>
            <span className="analytics-group-title">{lang === 'ar' ? 'الخدمات' : 'Services'}</span>
            <span className="analytics-group-chevron">{expandedGroups.services ? '\u25B2' : '\u25BC'}</span>
          </button>
          {expandedGroups.services && (
            <div className="analytics-group-content">
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'مزيج الخدمات (حجوزات)' : 'Service mix (bookings)'}</h4>
              {topServices.length > 0 ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Tooltip formatter={(v, name, props) => [`${v} (${((v / topServices.reduce((s, i) => s + i.value, 0)) * 100).toFixed(0)}%)`, props.payload?.name]} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Pie data={topServices} dataKey="value" nameKey="name" innerRadius={50} outerRadius={85} paddingAngle={2} stroke="#fff" strokeWidth={1} label={({ name, percent }) => percent > 0.08 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}>
                        {topServices.map((_, i) => {
                          const colors = ['#18442A', '#4CAF50', '#FF9800', '#2196F3', '#9C27B0'];
                          return <Cell key={i} fill={colors[i % colors.length]} />;
                        })}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{lang === 'ar' ? 'لا بيانات' : 'No data'}</p>}
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'معدل إلغاء الخدمات' : 'Cancellation by service'}</h4>
              {servicePerformance.slice(0, 5).some(s => s.cancellationRate > 0) ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={servicePerformance.slice(0, 5)} margin={{ top: 8, right: 16, left: 8, bottom: 70 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#667085' }} angle={-30} textAnchor="end" height={55} />
                      <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: '#667085' }} />
                      <Tooltip formatter={(v) => [`${v.toFixed(1)}%`, 'Cancellation']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Bar dataKey="cancellationRate" fill="#F44336" radius={[4, 4, 0, 0]} name={lang === 'ar' ? 'إلغاء %' : 'Cancellation %'} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{servicePerformance.length ? servicePerformance.map(s => `${s.name}: ${s.cancellationRate.toFixed(0)}%`).join('; ') : (lang === 'ar' ? 'لا بيانات' : 'No data')}</p>}
            </div>
          )}
        </div>

        {/* ─── Staff (Staff utilization, Staff revenue chart) ─── */}
        <div className="analytics-group chart-card chart-card--wide">
          <button type="button" className="analytics-group-toggle" onClick={() => setExpandedGroups(g => ({ ...g, staff: !g.staff }))} aria-expanded={expandedGroups.staff}>
            <span className="analytics-group-title">{lang === 'ar' ? 'الموظفون' : 'Staff'}</span>
            <span className="analytics-group-chevron">{expandedGroups.staff ? '\u25B2' : '\u25BC'}</span>
          </button>
          {expandedGroups.staff && (
            <div className="analytics-group-content">
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'إيرادات الموظفين' : 'Revenue by staff'}</h4>
              {revenueByStaff.length > 0 ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={revenueByStaff} margin={{ top: 12, right: 16, left: 12, bottom: 70 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#667085' }} angle={-25} textAnchor="end" height={55} />
                      <YAxis tickFormatter={(v) => (v / 1000).toFixed(0) + 'k'} tick={{ fontSize: 11, fill: '#667085' }} />
                      <Tooltip formatter={(v) => [CURRENCY(v) + ' EGP', 'Revenue']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Bar dataKey="value" fill="var(--primary)" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{lang === 'ar' ? 'لا بيانات' : 'No data'}</p>}
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'حجوزات الموظفين' : 'Bookings by staff'}</h4>
              {bookingsByStaff.length > 0 ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={bookingsByStaff} margin={{ top: 8, right: 16, left: 8, bottom: 70 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#667085' }} angle={-25} textAnchor="end" height={55} />
                      <YAxis tick={{ fontSize: 11, fill: '#667085' }} />
                      <Tooltip formatter={(v) => [v, 'Bookings']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Bar dataKey="value" fill="#4CAF50" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{bookingsByStaff.length ? `${bookingsByStaff.length} ${lang === 'ar' ? 'موظفين لديهم حجوزات' : 'staff with bookings'}` : (lang === 'ar' ? 'لا بيانات' : 'No data')}</p>}
            </div>
          )}
        </div>

        {/* ─── Revenue (Forecast, by day of week, by month/quarter, Average transaction value) ─── */}
        <div className="analytics-group chart-card chart-card--wide">
          <button type="button" className="analytics-group-toggle" onClick={() => setExpandedGroups(g => ({ ...g, revenue: !g.revenue }))} aria-expanded={expandedGroups.revenue}>
            <span className="analytics-group-title">{lang === 'ar' ? 'الإيرادات' : 'Revenue'}</span>
            <span className="analytics-group-chevron">{expandedGroups.revenue ? '\u25B2' : '\u25BC'}</span>
          </button>
          {expandedGroups.revenue && (
            <div className="analytics-group-content">
              {forecastData.forecast > 0 && (
                <div className="analytics-insight-card">
                  <span className="insight-label">{lang === 'ar' ? 'توقعات الإيرادات' : 'Revenue forecast'}</span>
                  <span className="insight-value">{CURRENCY(Math.round(forecastData.forecast))} EGP</span>
                  <p className="analytics-subsection-desc" style={{ marginBottom: 0 }}>{lang === 'ar' ? 'الاتجاه' : 'Trend'}: {forecastData.trend}</p>
                </div>
              )}
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'الإيرادات حسب يوم الأسبوع' : 'Revenue by day of week'}</h4>
              {revenueByDayOfWeek.some(d => d.value > 0) ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={revenueByDayOfWeek} margin={{ top: 10, right: 20, left: 20, bottom: 5 }}>
                      <defs>
                        <linearGradient id="revenueDayGrad" x1="0" y1="1" x2="0" y2="0">
                          <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.4}/>
                          <stop offset="100%" stopColor="var(--primary)" stopOpacity={1}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#667085' }} />
                      <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fill: '#667085' }} />
                      <Tooltip formatter={(v) => [CURRENCY(v) + ' EGP', 'Revenue']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Bar dataKey="value" fill="url(#revenueDayGrad)" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{lang === 'ar' ? 'لا بيانات' : 'No data'}</p>}
              <h4 className="analytics-subsection-title">{lang === 'ar' ? 'الإيرادات حسب الشهر' : 'Revenue by month/quarter'}</h4>
              {revenueByMonthQuarter.some(d => d.value > 0) ? (
                <div className="analytics-chart-wrap">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={revenueByMonthQuarter} margin={{ top: 10, right: 20, left: 20, bottom: 30 }}>
                      <defs>
                        <linearGradient id="revenueMonthGrad" x1="0" y1="1" x2="0" y2="0">
                          <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.4}/>
                          <stop offset="100%" stopColor="var(--primary)" stopOpacity={1}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#667085' }} angle={-35} textAnchor="end" height={50} />
                      <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fill: '#667085' }} />
                      <Tooltip formatter={(v) => [CURRENCY(v) + ' EGP', 'Revenue']} contentStyle={{ borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }} />
                      <Bar dataKey="value" fill="url(#revenueMonthGrad)" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="analytics-subsection-desc">{lang === 'ar' ? 'لا بيانات' : 'No data'}</p>}
              <div className="analytics-insight-card">
                <span className="insight-label">{lang === 'ar' ? 'متوسط قيمة المعاملة' : 'Average transaction value'}</span>
                <span className="insight-value">{CURRENCY(kpis.avgBookingValue)} EGP</span>
              </div>
            </div>
          )}
        </div>

        {/* ─── Period comparison (WoW / MoM / YoY) ─── */}
        <div className="chart-card chart-card--wide">
          <h3>{lang === 'ar' ? 'مقارنة الفترات' : 'Period comparison'}</h3>
          <div className="analytics-compare-btns">
            {['wow', 'mom', 'yoy'].map(key => (
              <button key={key} type="button" className={`mini ${comparisonPeriod === key ? 'active' : ''}`} onClick={() => setComparisonPeriod(comparisonPeriod === key ? null : key)}>
                {key.toUpperCase()}
              </button>
            ))}
          </div>
          {periodComparison && (
            <div className="period-comparison-box">
              <div className="period-comparison-item">
                <strong>{lang === 'ar' ? 'الحجوزات' : 'Bookings'}</strong>
                <span className="comparison-value">{periodComparison.bookings.current} vs {periodComparison.bookings.previous}</span>
                <span className={`comparison-change ${periodComparison.bookings.isPositive ? 'positive' : ''}`} style={{ color: periodComparison.bookings.isPositive ? '#047857' : '#B91C1C' }}>{periodComparison.bookings.change >= 0 ? '+' : ''}{periodComparison.bookings.change.toFixed(1)}%</span>
              </div>
              <div className="period-comparison-item">
                <strong>{lang === 'ar' ? 'الإيرادات' : 'Revenue'}</strong>
                <span className="comparison-value">{CURRENCY(periodComparison.revenue.current)} vs {CURRENCY(periodComparison.revenue.previous)}</span>
                <span className={`comparison-change ${periodComparison.revenue.isPositive ? 'positive' : ''}`} style={{ color: periodComparison.revenue.isPositive ? '#047857' : '#B91C1C' }}>{periodComparison.revenue.change >= 0 ? '+' : ''}{periodComparison.revenue.change.toFixed(1)}%</span>
              </div>
            </div>
          )}
          {!comparisonPeriod && <p className="placeholder">{lang === 'ar' ? 'اختر WoW أو MoM أو YoY للمقارنة' : 'Select WoW, MoM or YoY to compare'}</p>}
        </div>

        {/* ─── Insights (Recommendations, Anomalies, Booking trend prediction) ─── */}
        <div className="analytics-group chart-card chart-card--wide">
          <button type="button" className="analytics-group-toggle" onClick={() => setExpandedGroups(g => ({ ...g, insights: !g.insights }))} aria-expanded={expandedGroups.insights}>
            <span className="analytics-group-title">{lang === 'ar' ? 'التوصيات والرؤى' : 'Insights & recommendations'}</span>
            <span className="analytics-group-chevron">{expandedGroups.insights ? '\u25B2' : '\u25BC'}</span>
          </button>
          {expandedGroups.insights && (
            <div className="analytics-group-content insights-content">
              <section className="insights-block">
                <h4 className="analytics-subsection-title">{lang === 'ar' ? 'توصيات قابلة للتنفيذ' : 'Actionable recommendations'}</h4>
                {recommendationsList.length > 0 ? (
                  <div className="insights-rec-cards">
                    {recommendationsList.slice(0, 5).map(rec => (
                      <div key={rec.id} className={`insights-rec-card insights-rec-card--${rec.priority || 'medium'}`}>
                        <div className="insights-rec-header">
                          <span className="insights-rec-title">{rec.title}</span>
                          {rec.priority && <span className={`insights-rec-pill insights-rec-pill--${rec.priority}`}>{rec.priority}</span>}
                        </div>
                        <p className="insights-rec-message">{rec.message}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="analytics-subsection-desc">{lang === 'ar' ? 'لا توصيات حالياً' : 'No recommendations'}</p>}
              </section>
              <section className="insights-block">
                <h4 className="analytics-subsection-title">{lang === 'ar' ? 'الشذوذات' : 'Anomalies'}</h4>
                {anomaliesList.length > 0 ? (
                  <div className="insights-anomalies-grid">
                    {anomaliesList.slice(0, 5).map((a, i) => (
                      <div key={i} className={`insights-anomaly-chip insights-anomaly-chip--${a.type}`}>
                        <span className="insights-anomaly-type">{a.type === 'spike' ? (lang === 'ar' ? 'ارتفاع' : 'Spike') : (lang === 'ar' ? 'انخفاض' : 'Drop')}</span>
                        <span className="insights-anomaly-value">{CURRENCY(a.value)} EGP</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="analytics-subsection-desc">{lang === 'ar' ? 'لا شذوذات مكتشفة' : 'No anomalies detected'}</p>}
              </section>
              <div className="insights-metrics-row">
                {bookingTrendPrediction.predictedBookings > 0 && (
                  <div className="insights-metric-card">
                    <span className="insights-metric-label">{lang === 'ar' ? 'توقع اتجاه الحجوزات' : 'Booking trend prediction'}</span>
                    <span className="insights-metric-value">{bookingTrendPrediction.predictedBookings} {lang === 'ar' ? 'حجز' : 'bookings'}</span>
                    <span className="insights-metric-meta">{lang === 'ar' ? 'الاتجاه' : 'Trend'}: {bookingTrendPrediction.trend}, {lang === 'ar' ? 'الثقة' : 'confidence'}: {bookingTrendPrediction.confidence}</span>
                  </div>
                )}
                {customerLifetimeValue > 0 && customerAppointmentsCount > 0 && (
                  <div className="insights-metric-card">
                    <span className="insights-metric-label">{lang === 'ar' ? 'القيمة الدائمة للعميل (CLV)' : 'Customer lifetime value (CLV)'}</span>
                    <span className="insights-metric-value">{CURRENCY(customerLifetimeValue)} EGP</span>
                    <span className="insights-metric-meta">{customerAppointmentsCount} {lang === 'ar' ? 'عميل' : 'customers'}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
});
